import React, { useState, useEffect, useMemo } from 'react';
import { getLocale, useIntl } from 'umi';
import {
  Alert,
  Segment,
  Row,
  Col,
  Table,
  Progress,
  Button,
  Justify,
} from 'tea-component';
import ArrowBottom from '@/pages/intelligence/assets/svg/selectIcon/arrow-bottom.svg';
import '@/pages/intelligence/assets/scss/MarketProfile/TabDetails/Stats.scss';
import * as echarts from 'echarts';
import Loading from '@/pages/intelligence/components/Loading';
import NoData from '@/pages/intelligence/components/NoData';
import { drawLineChart2 } from '@/pages/intelligence/pages/MarketProfile/TabDetails/hooks/_echartsUtil';
import { formatNum } from '@/pages/intelligence/utils/commonUtils';
import ItemDom from '../../../TopCharts/Companies/components/GamesDetails/NewsActivity/MediaNews/ItemDom';

export default function MarketTabDetailsStats(props: any) {
  const {
    list,
    selectedKeys,
    gameTrendsData,
    gameType,
    color,
    pcColor,
    trendType,
    dataType,
    selectedKeysColor,
    sumRevenue,
    sumDownload,
    operationTrendsData,
  } = props;
  // console.log('MarketTabDetailsStats', props);

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const byDataTypeList = [
    { text: formatMessage({ id: 'dbi_market_operation_data' }), value: 'operation' },
    { text: formatMessage({ id: 'dbi_market_by_game' }), value: 'game' },
    { text: formatMessage({ id: 'dbi_market_by_genres' }), value: 'genre' },
    { text: formatMessage({ id: 'dbi_market_by_companies' }), value: 'company' },
  ];
  const byDataTypeStr = useMemo(() => {
    const byDataTypeObj = byDataTypeList?.find((item: any) => item.value == dataType) || {};
    return byDataTypeObj?.text;
  }, [dataType, locale]);
  const [subTrendType, setSubTrendType] = useState(trendType || 'revenue');
  const [segmentOptions, setSegmentOptions] = useState([]);

  useEffect(() => {
    const formatOptions = list?.subSegment?.map((item: any) => {
      return {
        ...item,
        text: formatMessage({ id: item.text })
      }
    })
    setSegmentOptions(formatOptions || [])
  }, [list, locale])

  const sumDesc = useMemo(() => {
    if (gameType === 'mobile' && dataType === 'operation') {
      return [
        { text: formatMessage({ id: 'dbi_company_metrics_sum_revenue' }), num: formatNum(sumRevenue, true) },
        { text: formatMessage({ id: 'dbi_calc_metric_sum_downloads' }), num: formatNum(sumDownload, true) },
      ]
    } else if (dataType === 'operation') {
      return [
        { text: 'Sum Revenue($) ', num: formatNum(sumRevenue, true) },
      ]
    } else {
      return null
    }

  }, [gameType, dataType, sumRevenue, sumDownload, locale])

  const resizeHandler = () => {
    let mapChartDom = document.getElementById('trendLine');
    if (mapChartDom) {
      let myMapChart = echarts.init(mapChartDom);
      try {
        myMapChart.resize();
      } catch (e) { }
    }
  };

  useEffect(() => {
    window.addEventListener('resize', resizeHandler);
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, []);


  useEffect(() => {
    const leftTitle = segmentOptions?.find((item: any) => item.value === (subTrendType || 'revenue'))?.text
    if (selectedKeys?.length > 0 && gameTrendsData['xAxis']) {
      let chartTempData = [] as any;
      const nameFormatterMap = {} as IRecord<string>;
      selectedKeys.map((key: any, index: number) => {
        let itemColor = '';
        if (gameType == 'mobile') {
          itemColor = color[index]
        } else {
          itemColor = pcColor[index]
        }
        let dataT = '';
        switch (trendType) {
          case 'revenue':
            dataT = 'revenue';
            break;
          case 'downloads':
            dataT = 'download';
            break;
          case 'revenueP':
            dataT = 'revenueP';
            break;
          case 'MAU':
            dataT = 'MAU';
            break;
          case 'digitalRevenue':
            dataT = 'digitalRevenue';
            break;
          case 'physicalRevenue':
            dataT = 'physicalRevenue';
            break;
          default:
            dataT = 'revenue';
            break;
        }
        let chartData = gameTrendsData[key.gameId];
        if (chartData) {
          const rawName = chartData.rawName || chartData['name'];
          nameFormatterMap[rawName] = chartData.chartDisplayName || rawName;
          let obj = {
            name: rawName,
            type: 'line',
            data: chartData[dataT],
            color: itemColor
          }
          chartTempData.push(obj);
        }
      })
      if (dataType != 'operation' && dataType != 'games') {
        drawLineChart2(
          'trendLine',
          chartTempData,
          gameTrendsData['xAxis'],
          leftTitle ? leftTitle : byDataTypeStr,
          (name: string) => nameFormatterMap[name] || name,
        );
      } else {
        drawLineChart2('trendLine', operationTrendsData['data'], operationTrendsData['xAxis'], byDataTypeStr);
      }
    } else {
      if (dataType === 'operation') {
        drawLineChart2('trendLine', operationTrendsData['data'], operationTrendsData['xAxis'], byDataTypeStr);
      }
    }
  }, [selectedKeys, selectedKeysColor, segmentOptions, gameTrendsData])

  const onTrendTypeChange = (value: string) => {
    setSubTrendType(value);
    if (selectedKeys.length > 0 && gameTrendsData['xAxis']) {
      let chartTempData = [] as any;
      const nameFormatterMap = {} as IRecord<string>;
      selectedKeys.map((key, index) => {
        let chartData = gameTrendsData[key.gameId];
        let itemColor = '';
        if (gameType == 'mobile') {
          itemColor = color[index]
        } else {
          itemColor = pcColor[index]
        }

        if (chartData) {
          const rawName = chartData.rawName || chartData['name'];
          nameFormatterMap[rawName] = chartData.chartDisplayName || rawName;
          let dataT = '';
          switch (value) {
            case 'revenue':
              dataT = 'revenue';
              break;
            case 'downloads':
              dataT = 'download';
              break;
            case 'revenueP':
              dataT = 'revenueP';
              break;
            case 'MAU':
              dataT = 'MAU';
              break;
            case 'digitalRevenue':
              dataT = 'digitalRevenue';
              break;
            case 'physicalRevenue':
              dataT = 'physicalRevenue';
              break;
            default:
              dataT = 'revenue';
              break;
          }
          let obj = {
            name: rawName,
            type: 'line',
            // data: chartData[value == 'revenue' ? 'revenue' : value == 'downloads' ? 'download' : value == 'revenueP' ? 'revenueP' : 'MAU'],
            data: chartData[dataT],
            color: itemColor
          }
          chartTempData.push(obj);
        }
      })
      const leftTitle = segmentOptions?.find((item: any) => item.value === value)?.text
      drawLineChart2(
        `trendLine`,
        chartTempData,
        gameTrendsData['xAxis'],
        leftTitle ? leftTitle : byDataTypeStr,
        (name: string) => nameFormatterMap[name] || name,
      );
    }
  };



  return (
    <div>
      <div className="dbi-stats-desc">
        {list?.subSegment ? (
          <Segment
            value={subTrendType}
            onChange={(value: any) => onTrendTypeChange(value)}
            options={segmentOptions}
          />
        ) : (
          ''
        )}
        {sumDesc?.map((item: any, index: number) => {
          return (
            <span key={index} className="dbi-stats-text">
              {item.text} : {item.num}
            </span>
          );
        })}
        {list?.text ? (
          <span className="dbi-stats-desc-text">
            {list.text}(
            <img src={ArrowBottom} className="dbi-stats-desc-icon" />)
          </span>
        ) : (
          ''
        )}
      </div>
      <div
        className="tabdetails-echarts"
        id={`trendLine`}
        style={{ height: 260 }}
      />
    </div>
  )
}
